// Plain fetch helpers for the BKFC backend endpoints. Not useHttp: the claim
// flow chains several sequential calls inside one user action, and useHttp's
// one-abort-controller-per-hook semantics (each call cancels the previous)
// would fight that. Base-URL resolution matches useHttp's.

const getBaseUrl = () =>
  process.env.REACT_APP_BASE_URL || (window as any).__BASE_URL__ || "";

export class BkfcRequestError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${getBaseUrl()}${path}`, {
      credentials: "include",
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers || {}),
      },
    });
  } catch {
    // fetch itself threw — the service is unreachable, not an HTTP error
    throw new BkfcRequestError(0, "Could not reach the signing service.");
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string | string[];
    } | null;
    const message = Array.isArray(body?.message)
      ? body!.message!.join(", ")
      : body?.message ?? response.statusText;
    throw new BkfcRequestError(response.status, message as string);
  }

  return (await response.json()) as T;
}

export interface BkfcClaimStatus {
  success: boolean;
  action: "ready_to_claim" | "already_claimed";
  uid: string;
  serial: number;
  network?: "testnet" | "mainnet";
  message: string;
}

export interface BkfcClaimSignature {
  recipient: string;
  uid: string;
  expirySecs: number;
  signature: string;
}

export interface BkfcConfirmMintResponse {
  success: boolean;
  action: "minted";
  uid: string;
  serial: number;
  tokenAddress: string | null;
  txHash: string | null;
  message: string;
}

export interface BkfcRaffleLinkResponse {
  success: boolean;
  message: string;
  cooldownSeconds?: number;
  raffleUrl?: string; // dev/testnet convenience only
}

export interface BkfcRaffleStatus {
  valid: boolean;
  reason?: "invalid_token" | "expired_token" | "not_claimed";
  drawn?: boolean;
  won?: boolean;
  rewardName?: string | null;
  email?: string;
  uid?: string;
}

export interface BkfcRaffleDrawResponse {
  success: boolean;
  action: "drawn" | "already_drawn";
  won: boolean;
  rewardName: string | null;
  message: string;
}

export interface BkfcCertificateStatus {
  uid: string;
  serial: number;
  claimedAt: string | null;
  tokenAddress: string | null;
  emailMasked: string | null;
  raffleDrawn: boolean;
  raffleWon: boolean | null;
  rewardName: string | null;
  raffleLinkSentAt: string | null;
}

export const fetchClaimStatus = (uid: string) =>
  request<BkfcClaimStatus>(`/bkfc/claim?uid=${encodeURIComponent(uid)}`);

export const fetchClaimSignature = (
  uid: string,
  recipient: string,
  email: string,
) =>
  request<BkfcClaimSignature>(`/bkfc/claim-signature`, {
    method: "POST",
    body: JSON.stringify({ uid, recipient, email }),
  });

export const confirmMint = (uid: string, txHash: string) =>
  request<BkfcConfirmMintResponse>(`/bkfc/confirm-mint`, {
    method: "POST",
    body: JSON.stringify({ uid, txHash }),
  });

export const sendRaffleLink = (uid: string) =>
  request<BkfcRaffleLinkResponse>(`/bkfc/raffle/send-link`, {
    method: "POST",
    body: JSON.stringify({ uid }),
  });

export const fetchRaffleStatus = (uid: string, token: string) =>
  request<BkfcRaffleStatus>(
    `/bkfc/raffle/status?uid=${encodeURIComponent(uid)}&token=${encodeURIComponent(token)}`,
  );

export const drawRaffle = (uid: string, token: string) =>
  request<BkfcRaffleDrawResponse>(`/bkfc/raffle/draw`, {
    method: "POST",
    body: JSON.stringify({ uid, token }),
  });

export const fetchCertificateStatuses = (address: string) =>
  request<{ success: boolean; certificates: BkfcCertificateStatus[] }>(
    `/bkfc/certificates?address=${encodeURIComponent(address)}`,
  );
